import { convertApiToLocal } from './ordensServicoConverter';
import {
  buildOrdemServicoUpsertRow,
  upsertOrdensServicoRows,
} from './ordensServicoSave';
import { clearClienteCache } from './ordensServicoCache';
import {
  loadOrdensSyncPreload,
  resolveClienteCadastroId,
} from './ordensServicoSyncPreload';

const CONVERT_CONCURRENCY = 64;
const UPSERT_CHUNK = 400;

function dedupeOrdensApi(ordensData: any[]): any[] {
  const byNumero = new Map<string, any>();
  for (const item of ordensData) {
    if (!item) continue;
    const n = String(item.numero ?? item.id ?? '');
    if (!n) continue;
    byNumero.set(n, item);
  }
  return Array.from(byNumero.values());
}

async function mapInConcurrency<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let i = 0;
  async function worker(): Promise<void> {
    while (i < items.length) {
      const idx = i++;
      out[idx] = await fn(items[idx], idx);
    }
  }
  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return out;
}

export const processBatchedData = async (
  ordensData: any[],
  impostoPercentual: number
): Promise<any[]> => {
  const unique = dedupeOrdensApi(ordensData);
  if (unique.length === 0) {
    throw new Error('Nenhuma ordem de serviço para processar');
  }

  const apiIds = unique.map((item) => item?.id).filter(Boolean);
  const duplicateApiIds = apiIds.filter((id, index) => apiIds.indexOf(id) !== index);
  if (duplicateApiIds.length > 0) {
    throw new Error(`Duplicatas encontradas nos dados da API: ${duplicateApiIds.join(', ')}`);
  }

  clearClienteCache();

  const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
  console.log(`[sync] Pré-carregando cadastros (${unique.length} OS)…`);
  const preload = await loadOrdensSyncPreload();
  const tPreload = typeof performance !== 'undefined' ? Math.round(performance.now() - t0) : 0;
  console.log(`[sync] Preload pronto em ~${tPreload}ms`);

  const convertedOrdens = await mapInConcurrency(
    unique,
    CONVERT_CONCURRENCY,
    async (apiItem) => {
      try {
        if (!apiItem) return null;
        const ordemServico = await convertApiToLocal(
          apiItem,
          impostoPercentual,
          preload
        );
        if (!ordemServico) return null;
        return { apiItem, ordemServico };
      } catch {
        return null;
      }
    }
  );

  const pairs = convertedOrdens.filter(Boolean) as {
    apiItem: any;
    ordemServico: any;
  }[];

  if (pairs.length === 0) {
    throw new Error('Nenhuma ordem de serviço foi convertida com sucesso');
  }

  const rows = pairs.map(({ apiItem, ordemServico }) => {
    const clienteId =
      resolveClienteCadastroId(ordemServico.cliente, preload) ??
      resolveClienteCadastroId(apiItem.empresa?.nome, preload);
    return buildOrdemServicoUpsertRow(apiItem, ordemServico, clienteId);
  });

  const tUpsert = typeof performance !== 'undefined' ? performance.now() : 0;
  console.log(`[sync] Gravando ${rows.length} OS em lotes de ${UPSERT_CHUNK}…`);
  await upsertOrdensServicoRows(rows, UPSERT_CHUNK);
  const tUpsertMs = typeof performance !== 'undefined' ? Math.round(performance.now() - tUpsert) : 0;
  console.log(`[sync] Upsert concluído em ~${tUpsertMs}ms`);

  const ordensOnly = pairs.map((p) => p.ordemServico);
  const convertedIds = ordensOnly.map((o) => o?.id).filter(Boolean);
  const finalDuplicateIds = convertedIds.filter((id, index) => convertedIds.indexOf(id) !== index);
  if (finalDuplicateIds.length > 0) {
    throw new Error(`Duplicatas encontradas nos dados convertidos: ${finalDuplicateIds.join(', ')}`);
  }

  return ordensOnly;
};
